/**
 * The text a ticketed event sends with each ticket. Leads write it in their own
 * words and language; placeholders are filled from the registration, the event
 * and the ticket when the text is queued. The server does the real filling
 * (private.render_ticket_message); this module mirrors it for the editor's
 * preview, so both must format values the same way.
 */

// Plain characters only: a curly apostrophe would send the whole text as Unicode,
// which fits under half as many characters.
export const DEFAULT_TICKET_MESSAGE = "Hi {{firstName}}, here's your ticket for {{eventName}} on {{startDateTime}}: {{ticketUrl}}"

export type TicketPlaceholder = 'firstName' | 'fullName' | 'eventName' | 'startDateTime' | 'endDateTime' | 'location' | 'ticketUrl'

/** Every placeholder a template may use, with the label the editor shows. */
export const TICKET_PLACEHOLDERS: ReadonlyArray<Readonly<{ key: TicketPlaceholder; label: string }>> = [
  { key: 'firstName', label: 'First name' },
  { key: 'fullName', label: 'Full name' },
  { key: 'eventName', label: 'Event name' },
  { key: 'startDateTime', label: 'Start date and time' },
  { key: 'endDateTime', label: 'End date and time' },
  { key: 'location', label: 'Location' },
  { key: 'ticketUrl', label: 'Ticket link' },
]

const known = new Set<string>(TICKET_PLACEHOLDERS.map((placeholder) => placeholder.key))
const token = /\{\{\s*([^{}]*?)\s*\}\}/g

/** The longest template accepted; a ticket link alone is over 100 characters. */
export const TICKET_MESSAGE_MAX_LENGTH = 1000

/** What's wrong with a template, in words for the editor, or null when it can be saved. */
export function ticketMessageProblem(template: string): string | null {
  if (!template.trim()) return 'Write the message, including {{ticketUrl}}.'
  const unknown = [...new Set([...template.matchAll(token)].map((match) => match[1] ?? '').filter((name) => !known.has(name)))]
  if (unknown.length > 0) return `${unknown.map((name) => `{{${name}}}`).join(', ')} ${unknown.length === 1 ? 'isn’t a placeholder' : 'aren’t placeholders'}. Use the buttons below to insert one.`
  if (![...template.matchAll(token)].some((match) => match[1] === 'ticketUrl')) return 'Include {{ticketUrl}}: it’s the link to their ticket.'
  if (template.length > TICKET_MESSAGE_MAX_LENGTH) return `Keep the message under ${TICKET_MESSAGE_MAX_LENGTH} characters.`
  return null
}

const dateFormat = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })

/**
 * "Sat 14 Nov, 18:30", or "Sat 14 Nov" with no time. Dates and times are the
 * event's own wall-clock values, so no time zone conversion happens here.
 */
export function formatEventDateTime(date: string | null, time: string | null): string {
  if (!date) return time ? time.slice(0, 5) : ''
  const day = dateFormat.format(new Date(`${date}T00:00:00Z`)).replace(',', '')
  return time ? `${day}, ${time.slice(0, 5)}` : day
}

export type TicketMessageValues = Readonly<Record<TicketPlaceholder, string>>

/** Fills every placeholder. */
export function renderTicketMessage(template: string, values: TicketMessageValues): string {
  return template.replace(token, (whole, name: string) => (known.has(name) ? values[name as TicketPlaceholder] : whole))
}

/** The event's values for a message, as the server will fill them. */
export function eventMessageValues(event: Readonly<{ name: string; eventDate: string; endDate: string | null; startTime: string | null; endTime: string | null; location: string }>) {
  const hasEnd = Boolean(event.endDate || event.endTime)
  return {
    eventName: event.name.trim(),
    startDateTime: formatEventDateTime(event.eventDate || null, event.startTime),
    endDateTime: hasEnd ? formatEventDateTime(event.endDate ?? event.eventDate, event.endTime) : '',
    location: event.location.trim().replace(/\s*\n\s*/g, ', '),
  }
}

// GSM-7, the alphabet a standard text uses. Anything outside it sends the
// whole message as Unicode, which fits far fewer characters per text.
const gsmBasic = new Set('@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà')
const gsmExtended = new Set('^{}\\[~]|€\f')

/** How many texts a message takes. An estimate: the provider has the final say. */
export function smsEstimate(text: string): Readonly<{ characters: number; segments: number; unicode: boolean }> {
  const characters = [...text]
  const unicode = characters.some((character) => !gsmBasic.has(character) && !gsmExtended.has(character))
  if (unicode) {
    const units = text.length
    return { characters: characters.length, segments: units <= 70 ? 1 : Math.ceil(units / 67), unicode }
  }
  const units = characters.reduce((total, character) => total + (gsmExtended.has(character) ? 2 : 1), 0)
  return { characters: characters.length, segments: units <= 160 ? 1 : Math.ceil(units / 153), unicode }
}
